import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../i18n/LanguageContext";
import { chiSiamo } from "../i18n/chisiamo";
import { insights } from "../i18n/insights";

const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="lang-switcher"
      className={`flex items-center rounded-full border border-slate-200 bg-white p-0.5 ${className}`}
    >
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono text-[0.68rem] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
            lang === l ? "bg-navy text-white" : "text-slate-400 hover:text-navy"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { lang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-link-home" },
    { to: "/servizi", label: t.nav.services, testid: "nav-link-services" },
    { to: "/chi-siamo", label: chiSiamo[lang].navLabel, testid: "nav-link-about" },
    { to: "/approfondimenti", label: insights[lang].navLabel, testid: "nav-link-insights" },
    { to: "/risk-analysis", label: t.nav.risk, testid: "nav-link-risk" },
    { to: "/regolamento-ue", label: t.nav.regulation, testid: "nav-link-regulation" },
    { to: "/industria-4-5", label: t.nav.industry, testid: "nav-link-industry" },
    { to: "/ai", label: t.nav.ai, testid: "nav-link-ai" },
    { to: "/contatti", label: t.nav.contacts, testid: "nav-link-contacts" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-slate-200/80 bg-white/85 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-white/0"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" aria-label="Aldebran home" data-testid="header-logo-link">
          <Logo compact={scrolled} />
        </Link>

        <nav className="hidden items-center gap-5 xl:gap-6 lg:flex" aria-label="Main">
          {links.slice(1).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group relative text-[0.8rem] font-semibold tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  isActive ? "text-navy" : "text-navy/80 hover:text-navy"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-navy transition-all duration-300 group-hover:w-full" />
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitch />
          <Link
            to="/contatti"
            data-testid="header-cta-button"
            className="group flex items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-[0.82rem] font-semibold text-white transition-colors duration-300 hover:bg-navy-dark"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangSwitch />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? t.nav.close : t.nav.menu}
            className="rounded-full border border-slate-200 bg-white p-2.5 text-navy"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-3 font-display text-lg font-semibold ${
                        isActive ? "text-navy" : "text-navy/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contatti"
                data-testid="mobile-header-cta-button"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3.5 font-semibold text-white"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
